<template>
    <Page>
        <PageHeader back="profile">
            {{ $t('pageTitle') }}
        </PageHeader>
        <div class="page-container double">
            <div class="">
                <PageBlock :title="$t('tariff')">
                    <div class="text-2xl text-prime-500 font-medium">{{ user.subscription.name[$i18n.locale] }}</div>
                    <div class="mt-11">
                        <div v-if="isEndless">{{ $t('neverExpire') }}</div>
                        <div v-if="willExpire" class="text-prime-500">
                            {{ $t('expire', { date: localizeNext }) }}
                        </div>
                        <div v-if="isPaid && ! willExpire && ! isEndless">
                            {{ 
                                user.subscription.price 
                                + ' ' + $t('currency.' + user.subscription.currency) 
                                + '/' + $t('periods.' + user.subscription.period)
                            }}
                            <div v-if="user.subscription.status == 'Active' &&  isPaid && ! isEndless" class="mt-11">{{ $t('nextPayment', { date: localizeNext }) }}</div>
                            <div v-if="user.subscription.status == 'PastDue'" class="mt-11">
                                <IconAlert height="20" classes="inline-block mb-5 text-prime-500" />
                                {{ $t('warnPastDue') }}
                            </div>
                        </div>
                    </div>
                    <div class="clearfix">
                        <div class="float-right">
                            <SaveButton editing="true" v-if="isPaid && ! isEndless"
                                :save="$t('subscription.cancel')" 
                                @save="cancel" />
                            <SaveButton editing="true"
                                :save="$t('subscription.upgrade')" 
                                @save="$router.push({ name: 'select-tariff' })" />
                        </div>
                    </div>
                </PageBlock>

                <PageBlock :title="$t('card.title')" v-if="user.cardLastFour">
                    ** {{ user.cardLastFour }}
                    <div class="clearfix">
                        <div class="float-right">
                            <SaveButton editing="true"
                                :save="$t('card.change')" 
                                @save="$router.push({ name: 'payment-card' })" />
                        </div>
                    </div>
                </PageBlock>
            </div>
            <div class="">
                <PageBlock :title="$t('history.title')">
                    <table class="table-auto w-full max-w-full" v-if="payments && payments.length">
                        <tr v-for="(payment, index) in paymentsLimited" :key="index" valign="top">
                            <td class="pb-11">{{ localizeDate(payment.created_at) }}</td>
                            <td class="text-right pb-11">
                                {{ payment.amount + ' ' + $t('currency.' + payment.currency) }}
                            </td>
                        </tr>
                    </table>
                    <div class="mt-11" v-if="payments && payments.length"><b>{{ $t('history.total') }}:</b> {{ payments.length }}</div>
                    <div class="text-center" v-if="! payments || ! payments.length">{{ $t('history.empty') }}</div>
                    <div class="clearfix" v-if="payments && payments.length">
                        <div class="float-right">
                            <SaveButton editing="true"
                                :save="$t('history.view')" 
                                @save="$router.push({ name: 'payments' })" />
                        </div>
                    </div>
                </PageBlock>
            </div>
        </div>
    </Page>
</template>

<script>
import PageHeader from '@/components/visual/PageHeader'
import PageBlock from '@/components/visual/PageBlock'
import Page from '@/components/visual/Page'
import SaveButton from '@/components/visual/SaveButton'
import IconAlert from '@/components/visual/icons/IconAlert'
import { mapState, mapGetters } from 'vuex'

export default {
    components: { Page, PageHeader, PageBlock, SaveButton, IconAlert },
    methods: {
        cancel() {
            this.$store.dispatch('subscription/cancel')
                .then(() => {
                    this.$store.dispatch('message/show' , this.$t('subscription.cancelled'))
                })
        },
        localizeDate(date) {
            return date ? this.$d(new Date(date), 'payment') : ''
        },
        openReceipt(url) {
            window.open(url, '_blank')
        },
    },
    computed: {
        willExpire() {
            return this.user.subscription.price == 0 && ! this.user.subscription.prolongable 
                && this.user.subscription.period != 'endless' || this.user.subscription.status == 'Cancelled'
        },
        isPaid() {
            return this.user.subscription.price > 0
        },
        isEndless() {
            return this.user.subscription.period == 'endless'
        },
        localizeNext() {
            return this.user.subscription.next_transaction_date 
                ? this.$d(new Date(this.user.subscription.next_transaction_date), 'short') 
                : ''
        },
        paymentsLimited() {
            return (this.payments && this.payments.length) ? this.payments.slice(0, 5) : []
        },
        ...mapGetters('errors', {errorsHappened: 'happened'}),
        ...mapGetters('auth', ['canUse']),
        ...mapState('auth', ['user']),
        ...mapState('subscription', ['payments']),
    },
    mounted() {
        this.$store.dispatch('locale/loadTariffs')
        this.$store.dispatch('locale/loadSubscriptions')
        this.$store.dispatch('subscription/loadPayments')
    },
}
</script>

<i18n locale="ru" lang="yaml">
    pageTitle: "Подписка"
    tariff: "Тарифный план"
    change: "Изменить"
    currency:
        RUB: "рублей"
    expire: "Закончится {date}"
    neverExpire: "Не закончится никогда"
    nextPayment: "Следующий автоматический платеж {date}"
    subscription: 
        upgrade: "Улучшить"
        cancel: "Отменить"
        cancelled: "Подписка отменена"
    card:
        title: "Сохраненная карта"
        change: "Изменить"
    history:
        title: "История платежей"
        empty: "Вы пока ничего не оплачивали"
        view: "Подробнее"
        total: "Всего платежей"       
    warnPastDue: "Оплата за подписку просрочена! Пополните карту или сохраните другую карту. Если программа не сможет списать оплату в ближайшие дни, то подписка будет отключена автоматически."
</i18n>

<i18n locale="en" lang="yaml">
    pageTitle: "Subscription"
    tariff: "Subscription plan"
    change: "Change"
    currency:
        RUB: "RUB"
    expire: "Will expire {date}"
    neverExpire: "Never expire"
    nextPayment: "Next payment {date}"
    subscription: 
        upgrade: "Улучшить"
        cancel: "Отменить"
        cancelled: "Подписка отменена"
    card:
        title: ""
        change: ""
</i18n>